import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { SunriseIcon, FlaskConicalIcon, EyeIcon, TreesIcon, ZapIcon, SkullIcon, SproutIcon, MapIcon, PuzzleIcon, RotateCcwIcon } from 'lucide-react';
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.07
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 20,
    scale: 0.97
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
interface ChapterCardProps {
  number: number;
  title: string;
  icon: React.ReactNode;
  accentColor: string;
}
function ChapterCard({
  number,
  title,
  icon,
  accentColor
}: ChapterCardProps) {
  return <motion.div variants={cardVariants} className="group relative" data-id="element-39">
      <div className="relative rounded-xl bg-white/[0.02] backdrop-blur-xl border border-white/[0.06] p-6 h-full overflow-hidden transition-colors duration-500 hover:bg-white/[0.04]" data-id="element-40">
        {/* Glow line at top */}
        <div className="absolute top-0 left-4 right-4 h-px opacity-40" style={{
        background: `linear-gradient(to right, transparent, ${accentColor}, transparent)`
      }} data-id="element-41" />

        <div className="flex items-start gap-4" data-id="element-42">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{
          backgroundColor: `${accentColor}15`
        }} data-id="element-43">
            {icon}
          </div>
          <div data-id="element-44">
            <p className="font-sans text-xs text-white/20 tracking-widest uppercase mb-1" data-id="element-45">
              Capítulo {number}
            </p>
            <h3 className="font-serif text-lg font-bold text-white" data-id="element-46">{title}</h3>
          </div>
        </div>
      </div>
    </motion.div>;
}
export function ChaptersSection() {
  return <section className="relative w-full py-24 sm:py-32" data-id="element-47">
      {/* Background glow */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-purple-electric/[0.03] rounded-full blur-[150px] pointer-events-none" data-id="element-48" />

      <div className="relative max-w-6xl mx-auto px-6" data-id="element-49">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} className="text-center mb-16" data-id="element-50">
          <p className="font-sans text-sm text-gold tracking-[0.2em] uppercase mb-4" data-id="element-51">
            10 capítulos
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-white" data-id="element-52">
            Lo que encontrarás dentro
          </h2>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-40px'
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-53">
          <ChapterCard number={1} title="El Despertar" icon={<SunriseIcon className="w-5 h-5 text-gold" data-id="element-55" />} accentColor="#d4a853" data-id="element-54" />
          <ChapterCard number={2} title="La Molécula Sagrada" icon={<FlaskConicalIcon className="w-5 h-5 text-purple-electric" data-id="element-57" />} accentColor="#8b5cf6" data-id="element-56" />
          <ChapterCard number={3} title="El Tercer Ojo" icon={<EyeIcon className="w-5 h-5 text-astral" data-id="element-59" />} accentColor="#3b82f6" data-id="element-58" />
          <ChapterCard number={4} title="Voces de la Selva" icon={<TreesIcon className="w-5 h-5 text-green-400" data-id="element-61" />} accentColor="#4ade80" data-id="element-60" />
          <ChapterCard number={5} title="El Puente Cuántico" icon={<ZapIcon className="w-5 h-5 text-astral" data-id="element-63" />} accentColor="#3b82f6" data-id="element-62" />
          <ChapterCard number={6} title="Muerte del Ego" icon={<SkullIcon className="w-5 h-5 text-purple-electric" data-id="element-65" />} accentColor="#8b5cf6" data-id="element-64" />
          <ChapterCard number={7} title="El Lenguaje de las Plantas" icon={<SproutIcon className="w-5 h-5 text-green-400" data-id="element-67" />} accentColor="#4ade80" data-id="element-66" />
          <ChapterCard number={8} title="Cartografía Interior" icon={<MapIcon className="w-5 h-5 text-gold" data-id="element-69" />} accentColor="#d4a853" data-id="element-68" />
          <ChapterCard number={9} title="La Integración" icon={<PuzzleIcon className="w-5 h-5 text-gold" data-id="element-71" />} accentColor="#d4a853" data-id="element-70" />
          <ChapterCard number={10} title="El Retorno" icon={<RotateCcwIcon className="w-5 h-5 text-purple-electric" data-id="element-73" />} accentColor="#8b5cf6" data-id="element-72" />
        </motion.div>
      </div>
    </section>;
}